declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}

export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID

export const initializeGTM = () => {
  if (!GTM_ID || GTM_ID === 'GTM-PLACEHOLDER') return

  // Guard against double initialization
  if (document.querySelector(`script[src*="${GTM_ID}"]`)) return

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as any)
  }

  // Set default consent state BEFORE loading GTM script (Consent Mode v2)
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  })

  // gtm.start must be pushed BEFORE script load
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  })

  // Load GTM script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.appendChild(script)

  // Add GTM noscript fallback
  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)
  document.body.appendChild(noscript)
}

export const updateGTMConsent = (accepted: boolean) => {
  if (!window.gtag) return

  window.gtag('consent', 'update', {
    analytics_storage: accepted ? 'granted' : 'denied',
    functionality_storage: accepted ? 'granted' : 'denied',
    ad_storage: 'denied',
    personalization_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })

  window.gtag('event', 'cookie_consent', {
    event_category: 'privacy',
    event_label: accepted ? 'accepted' : 'declined',
    value: accepted ? 1 : 0,
  })
}

export const hasGTMConsent = (): boolean | null => {
  const consent = localStorage.getItem('gtm_consent')
  if (consent === null) return null
  return consent === 'true'
}

export const setGTMConsent = (accepted: boolean) => {
  localStorage.setItem('gtm_consent', accepted.toString())
  localStorage.setItem('gtm_consent_timestamp', Date.now().toString())
  updateGTMConsent(accepted)
}

export const trackPageView = (url: string) => {
  if (typeof window === 'undefined' || !window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: url,
  })
}

export const isMainApplication = (): boolean => {
  if (typeof window === 'undefined') return false

  const currentUrl = window.location.href
  const currentPathname = window.location.pathname

  const isWidgetTestPage =
    currentUrl.includes('chatbot_page.html') ||
    currentUrl.startsWith('file://') ||
    currentPathname.includes('widget-embed') ||
    currentPathname.includes('/embed/') ||
    window.location.search.includes('widgetId') ||
    window.location.search.includes('chatbotId')

  if (isWidgetTestPage) return false

  const appUrl = process.env.NEXT_PUBLIC_APP_URL
  if (!appUrl) {
    return window.location.hostname === 'localhost'
  }

  try {
    const appOrigin = new URL(appUrl).origin
    return window.location.origin === appOrigin
  } catch {
    return false
  }
}

export const shouldShowConsentBanner = (): boolean => {
  if (!isMainApplication()) return false

  const consent = hasGTMConsent()
  if (consent === null) return true

  const timestamp = localStorage.getItem('gtm_consent_timestamp')
  if (timestamp) {
    const consentAge = Date.now() - parseInt(timestamp)
    const thirteenMonths = 13 * 30 * 24 * 60 * 60 * 1000
    if (consentAge > thirteenMonths) {
      localStorage.removeItem('gtm_consent')
      localStorage.removeItem('gtm_consent_timestamp')
      return true
    }
  }

  return false
}
